import { monotonicNow } from './monotonicNow';

/**
 * Shared clock + curve for the thin planning loading bar.
 *
 * The bar is painted four times during a cold load — the static preboot shell
 * in public/index.html, the lazy-route Suspense fallback, the auth-check shell
 * in ProtectedRoute, and finally the workspace gate overlay — so every React
 * instance reads from ONE module-level clock and never renders a lower value
 * than the previous instance. The preboot script stamps `__planningPrebootAt`
 * so the first React bar continues the CSS curve instead of restarting.
 */
export const TRICKLE_CEILING = 90;
export const TRICKLE_TAU_MS = 2500;
/** Handoffs between shells commit back-to-back; anything slower is a new load. */
export const CLOCK_HANDOFF_GRACE_MS = 400;

type PrebootWindow = typeof globalThis & { __planningPrebootAt?: unknown };

const now = monotonicNow;

/** Asymptotic trickle toward TRICKLE_CEILING; mirrors the preboot keyframes. */
export const trickleProgress = (elapsedMs: number): number => (
  TRICKLE_CEILING * (1 - Math.exp(-Math.max(0, elapsedMs) / TRICKLE_TAU_MS))
);

const readPrebootEpoch = (): number | null => {
  const stamped = (globalThis as PrebootWindow).__planningPrebootAt;

  return typeof stamped === 'number' && Number.isFinite(stamped) ? stamped : null;
};

let epochMs: number | null = null;
let mountedBars = 0;
let lastReleaseAt = Number.NEGATIVE_INFINITY;
let highWater = 0;

const restartClock = (at: number) => {
  epochMs = at;
  highWater = 0;
};

/**
 * Lazily (re)start the clock. It restarts only when no bar has been mounted
 * for longer than the handoff grace — a genuinely new load — never during the
 * preboot → Suspense → auth → gate relay of a single cold start.
 */
const ensureClock = () => {
  const at = now();

  if (epochMs === null) restartClock(readPrebootEpoch() ?? at);
  else if (mountedBars === 0 && Number.isFinite(lastReleaseAt) && at - lastReleaseAt > CLOCK_HANDOFF_GRACE_MS) {
    // Only a bar that has come and gone can make this a "new load"; reads
    // before the very first acquire (a bar's render-time seed) keep the epoch.
    restartClock(at);
  }
};

/** Register a mounted bar; the returned function unregisters it. */
export const acquirePlanningLoadingClock = (): (() => void) => {
  ensureClock();
  mountedBars += 1;

  return () => {
    mountedBars -= 1;
    lastReleaseAt = now();
  };
};

/** Force a fresh curve — the workspace gate calls this when it re-arms. */
export const restartPlanningLoadingClock = () => restartClock(now());

/**
 * Current bar value: the greater of the time trickle and the caller's floor,
 * never lower than what any bar already showed on this clock. Safe to call
 * before `acquire` (a bar's first render) — it settles the clock itself.
 */
export const readPlanningLoadingProgress = (floor = 0): number => {
  ensureClock();
  const elapsed = now() - (epochMs as number);
  const next = Math.min(100, Math.max(trickleProgress(elapsed), floor, highWater));

  highWater = next;

  return next;
};

/** Test-only reset of the module clock. */
export const resetPlanningLoadingClockForTests = () => {
  epochMs = null;
  mountedBars = 0;
  lastReleaseAt = Number.NEGATIVE_INFINITY;
  highWater = 0;
};
